import { useEffect, useRef, useState } from "react";
import { SectionSeam } from "@/components/SectionSeam";

type Milestone = { age: string; tag: string; text: string; stat?: { value: number; suffix: string; label: string }; photo?: boolean; names?: string[] };

const MILESTONES: Milestone[] = [
  { age: "16", tag: "Signal 01 / First bass", text: "Picked up a bass for the first time and fell in love with music.", photo: true },
  { age: "18", tag: "Signal 02 / Signed & dropped", text: "Signed to a major record label, and dropped six months later. I always said I'd do anything, but no one told me what to do. That sparked my obsession with understanding how artists actually succeed." },
  { age: "23", tag: "Signal 03 / BIMM founded", text: "Founded BIMM music university, which became one of the largest music universities in the world.", photo: true },
  { age: "20s", tag: "Signal 04 / Scale", text: "Scaled BIMM to 4,000 students per year across multiple countries, while performing as a professional bass player with Eric Clapton, Alanis Morissette and Billy Cobham among others.", stat: { value: 4000, suffix: "", label: "Students / year" }, names: ["Eric Clapton", "Alanis Morissette", "Billy Cobham"], photo: true },
  { age: "30", tag: "Signal 05 / Exit", text: "Sold BIMM and became financially free." },
  { age: "30s", tag: "Signal 06 / Build", text: "Realised life is about the journey and building, not retiring. Built multiple music businesses including a management company and live agency." },
  { age: "40s", tag: "Signal 07 / Online", text: "Sold both businesses and took what I'd learned working with artists offline and applied it online, helping artists grow with digital strategy before it became common." },
  { age: "43", tag: "Signal 08 / DK-MBA", text: "Founded DK-MBA, now one of the largest online music business academies.", stat: { value: 40000, suffix: "+", label: "Students / 83+ countries" }, photo: true },
  { age: "45", tag: "Signal 09 / DK Artists", text: "Founded DK Artists, where we work directly with independent and label artists building careers, audiences and releases." },
  { age: "Today", tag: "Signal 10 / Engineered", text: "We work with independent and label artists, building the systems and infrastructure that help artists grow streams, audiences and long-term careers.", photo: true },
];

const N = MILESTONES.length;

function Counter({ to, suffix, active }: { to: number; suffix: string; active: boolean }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf = 0; const t0 = performance.now();
    const tick = (t: number) => { const k = Math.min(1, (t - t0) / 1600); setV(Math.round(to * (1 - Math.pow(1 - k, 3)))); if (k < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, to]);
  return <>{v.toLocaleString()}{suffix}</>;
}

function withNames(text: string, names?: string[]) {
  if (!names?.length) return text;
  const re = new RegExp(`(${names.join("|")})`, "g");
  return text.split(re).map((part, i) => names.includes(part) ? <span key={i} className="journey-static" data-text={part}>{part}</span> : part);
}

export function JourneySection() {
  const wrap = useRef<HTMLElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [progress, setProgress] = useState(0);
  const prog = useRef(0);
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const onScroll = () => {
      const el = wrap.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, -r.top / (r.height - window.innerHeight)));
      prog.current = p; setProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Soundwave line: slow breathing, pluck at each milestone, snappy cursor bend.
  useEffect(() => {
    const c = canvas.current; if (!c || reduced) return;
    const ctx = c.getContext("2d")!;
    let raf = 0, w = 0, h = 0, pluck = 0, lastIdx = -1;
    const mouse = { x: -1, y: -1, bend: 0 };
    const resize = () => { const d = Math.min(2, devicePixelRatio); w = c.clientWidth; h = c.clientHeight; c.width = w * d; c.height = h * d; ctx.setTransform(d, 0, 0, d, 0, 0); };
    const move = (e: PointerEvent) => { const r = c.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; };
    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", move);
    const draw = (t: number) => {
      const p = prog.current, idx = Math.round(p * (N - 1));
      if (idx !== lastIdx) { pluck = 1; lastIdx = idx; }
      pluck *= 0.975;
      ctx.clearRect(0, 0, w, h);
      const dark = p > 0.55;
      const base = h * 0.78;
      const targetBend = mouse.y > 0 ? Math.max(-1, Math.min(1, (mouse.y - base) / 120)) : 0;
      mouse.bend += (targetBend - mouse.bend) * 0.35;
      for (let k = 0; k < 3; k++) {
        ctx.beginPath();
        for (let x = 0; x <= w; x += 4) {
          const u = x / w, s = t * 0.0004 + k;
          const head = Math.exp(-Math.pow((u - p) * 6, 2));
          const cur = mouse.x >= 0 ? Math.exp(-Math.pow((x - mouse.x) / 160, 2)) * mouse.bend * 40 : 0;
          const y = base + Math.sin(u * 9 + s) * (6 + k * 3) + Math.sin(u * 40 - t * 0.003) * head * (8 + pluck * 34) + cur + k * 10;
          x ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        }
        const a = k === 0 ? 0.55 : 0.18;
        ctx.strokeStyle = dark ? `hsla(205,90%,65%,${a})` : `hsla(220,30%,20%,${a * 0.6})`;
        ctx.lineWidth = k === 0 ? 1.4 : 1;
        if (k === 0 && dark) { ctx.shadowColor = "hsla(205,100%,60%,0.8)"; ctx.shadowBlur = 12; } else ctx.shadowBlur = 0;
        ctx.stroke();
      }
      // progress marker
      ctx.shadowBlur = 0;
      ctx.fillStyle = "hsla(25,90%,55%,0.9)";
      ctx.beginPath(); ctx.arc(p * w, base, 3 + pluck * 3, 0, Math.PI * 2); ctx.fill();
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); window.removeEventListener("pointermove", move); };
  }, [reduced]);

  if (reduced) {
    return (
      <section id="journey" className="relative bg-hero-human py-24">
        <SectionSeam label="02 Journey" />
        <div className="max-w-4xl mx-auto px-8 space-y-10">
          <h2 className="text-5xl font-semibold tracking-tight text-[hsl(var(--hero-ink))]">A bit about my journey…</h2>
          {MILESTONES.map((m) => <div key={m.age} className="grid grid-cols-[120px_1fr] gap-6"><span className="text-4xl font-semibold">{m.age}</span><p className="font-mono text-sm leading-relaxed">{m.text}</p></div>)}
        </div>
      </section>
    );
  }

  const active = Math.min(N - 1, Math.round(progress * (N - 1)));
  const darkMix = Math.min(1, Math.max(0, (progress - 0.35) / 0.4));
  const m = MILESTONES[active];

  return (
    <section ref={wrap} id="journey" data-nav-theme={darkMix > 0.5 ? "dark" : "light"} className="relative" style={{ height: `${N * 70}vh` }}>
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* human -> machine background */}
        <div className="absolute inset-0 bg-hero-human" />
        <div className="absolute inset-0 bg-[hsl(0_0%_3%)] transition-opacity duration-700" style={{ opacity: darkMix }} />
        <div className="absolute inset-0 pointer-events-none" style={{ opacity: darkMix, background: "radial-gradient(60% 50% at 70% 60%, hsl(205 90% 50% / 0.14), transparent 70%)" }} />
        <SectionSeam label="02 Journey" dark={darkMix > 0.5} />
        <canvas ref={canvas} className="absolute inset-0 h-full w-full" />

        <div className="relative z-10 mx-auto grid h-full max-w-7xl grid-cols-[1.1fr_1fr] items-center gap-16 px-10" style={{ color: darkMix > 0.5 ? "hsl(0 0% 96%)" : "hsl(var(--hero-ink))" }}>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] opacity-50 mb-6">A bit about my journey…</p>
            <div key={m.age} className="journey-in">
              <div className="font-semibold leading-[0.85] tracking-[-0.05em]" style={{ fontSize: "clamp(7rem, 16vw, 15rem)" }}>{m.age}</div>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-[hsl(205_80%_55%)]">{m.tag}</p>
            </div>
          </div>
          <div key={`t-${m.age}`} className="journey-in max-w-md">
            {m.photo && (
              <div className="journey-photo mb-8 aspect-[4/3] w-full rounded-sm border flex items-end p-4" style={{ borderColor: darkMix > 0.5 ? "hsl(0 0% 100% / 0.15)" : "hsl(var(--hero-ink) / 0.15)" }}>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-40">Photo / age {m.age}</span>
              </div>
            )}
            <p className="text-xl leading-relaxed">{withNames(m.text, m.names)}</p>
            {m.stat && (
              <div className="mt-8">
                <div className="text-5xl font-semibold tracking-tight"><Counter to={m.stat.value} suffix={m.stat.suffix} active /></div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] opacity-50">{m.stat.label}</div>
              </div>
            )}
          </div>
        </div>

        {/* milestone ticks */}
        <div className="absolute bottom-10 inset-x-10 z-10 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em]">
          {MILESTONES.map((x, i) => (
            <span key={x.age} className="transition-opacity duration-500" style={{ opacity: i === active ? 1 : 0.3, color: darkMix > 0.5 ? "hsl(0 0% 96%)" : "hsl(var(--hero-ink))" }}>{x.age}</span>
          ))}
        </div>
      </div>
    </section>
  );
}
